/**
 * Learn Mode — the pretend deal.
 *
 * One property, one owner, one buyer, carried across every lesson so the
 * arithmetic at the end is one the agent watched being built:
 *   RM 680,000 sold → RM 13,600 fee → 70% tier → RM 9,520, company keeps RM 4,080.
 *
 * Shapes here match the agent server's real mappers, not `src/api/mocks.js` —
 * that file predates the server and is wrong in ways that would render blank
 * screens (no `edit_level`, no `settable_stages`, wrong pagination keys).
 *
 * Every reference carries DEMO and every party carries "(demo)". A practice
 * record that leaves the phone as a screenshot is the failure that actually
 * costs money, so the markers live in the data and not only in the chrome.
 */

const ID = {
  listing: 'demo-listing-0001',
  als: 'demo-form-als-001',
  otp: 'demo-form-otp-001',
  sale: 'demo-sale-0001',
  csr: 'demo-coshare-req-01',
  csform: 'demo-coshare-form-01',
};

/**
 * The other REN in the co-share lesson. Same company, not a co-agency — the whole
 * point of the lesson is that distinction, so they carry a CID REN number.
 */
const PEER = {
  id: 'demo-agent-peer',
  name: 'Farah binti Idris (demo)',
  ren: 'REN 55555',
  branch: 'Petaling Jaya',
};

/**
 * What the agent personally ends up with, which is the ONE figure the two lessons
 * disagree about. Sharing a deal halves the fee before the tier is applied, so the
 * same house pays RM 9,520 alone and RM 4,760 shared — and every screen that
 * states a number has to agree, or the lesson teaches arithmetic it then breaks.
 */
export const MY = () => {
  const mine = S && S.mode === 'coshare' ? (100 - S.csSplit) / 100 : 1;
  const net = round2(DEAL.fee * mine);
  return { net, share: round2(net * 0.7), pct: mine * 100 };
};
const round2 = (n) => Math.round(n * 100) / 100;
const RMF = (n) => `RM ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const DEAL = {
  property: 'Residensi Amanina, A-12-03',
  address: 'A-12-03, Residensi Amanina, Jalan Amanina 3',
  area: 'Petaling Jaya',
  state: 'Selangor',
  postcode: '46000',
  price: 680000,
  owner: 'Lim Wei Sheng (demo)',
  buyer: 'Nurul Aisyah bt Rahman (demo)',
  fee: 13600,
  share: 9520,
  residual: 4080,
  earnest: 20400,
};

/** Mutable so an HQ beat can flip a status mid-lesson. Reset on every start. */
let S = null;

export function resetFixtures(lessonId) {
  const coshare = lessonId === 'coshare';
  // The paperwork-chain lesson: an ALS the owner has already signed, and NO
  // listing yet — the agent makes the listing from the letter, which is the
  // whole point of that lesson.
  const authchain = lessonId === 'authchain';
  S = {
    // Which pretend deal this is. 'coshare' turns the same house into a shared
    // one: two RENs on the sale, half the fee, and the co-share paperwork.
    mode: coshare ? 'coshare' : authchain ? 'auth' : 'solo',
    listingStatus: 'ACTIVE',
    listingVisibility: 'PUBLIC',
    saleStatus: 'Pending',
    saleStage: 'DRAFT',
    saleStageLabel: 'Draft',
    commissionChip: 'Locked',
    invoiceRaised: false,
    paid: false,
    // The OTP starts unsigned so the lesson can walk the real signing flow.
    otpSigned: [],           // signatory keys that have signed
    otpStatus: 'DRAFT',      // DRAFT -> PENDING_SIGNING -> SIGNED -> APPROVED
    // Parties the agent creates during the lesson. The lookup starts EMPTY on
    // purpose: with nothing to pick, "＋ Create new" is the only way forward, so
    // the step teaches adding a contact instead of choosing a ready-made one.
    contacts: [],
    // Receipts the agent records during the lesson.
    receipts: [],
    /* ── the co-share lesson ── */
    // PENDING (they have asked) → ACCEPTED (you said yes) → CONVERTED (sale raised)
    csStatus: 'PENDING',
    // Their share of the fee. Settled by the agent in the accept sheet, and it is
    // what seeds the sale's split — so the whole lesson's money follows from it.
    csSplit: 50,
    csSaleCreated: false,
    // null → DRAFT → PENDING_SIGNING → SIGNED
    csForm: null,
    csFormSigned: [],
    /* ── the paperwork-chain lesson ── */
    // The listing does not exist until the agent converts the letter into one.
    listingCreated: false,
    // …and Publish is a SECOND call after that. The step waits on THIS, so a
    // refused publish cannot be walked past as though it had worked.
    listingPublished: false,
    // Whether the agent's booking form actually saved. The save step waits on
    // THIS, not on the tap: the form has twelve mandatory fields and the app
    // refuses an incomplete one, so a tour that advanced on the tap taught a
    // save that never happened.
    formCreated: false,
    // Which route the agent took to the form — set by the fork at the start of
    // the lesson. A form written from scratch is linked to no listing, so
    // issuing it books nothing.
    fromListing: true,
  };
  if (authchain) {
    // The owner is already a contact — they signed the letter, so of course they
    // are in the book. The buyer is not: nobody has made an offer yet.
    S.contacts = [
      { id: 'demo-contact-v', name: DEAL.owner, type: 'VENDOR', entity_type: 'INDIVIDUAL', sub_type: 'VENDOR', registration_no: '640909-10-5099', contact_no: '012-000 0000', is_mine: true, creator_name: 'You' },
    ];
  }
  if (coshare) {
    // The vendor and the buyer already exist here. Creating a contact is the
    // SPINE lesson's job, and repeating it would bury the co-share under eight
    // typing steps — by the time an agent shares a deal they have a contact book.
    S.contacts = [
      { id: 'demo-contact-v', name: DEAL.owner, type: 'VENDOR', entity_type: 'INDIVIDUAL', sub_type: 'VENDOR', registration_no: '640909-10-5099', contact_no: '012-000 0000', is_mine: true, creator_name: 'You' },
      { id: 'demo-contact-p', name: DEAL.buyer, type: 'PURCHASER', entity_type: 'INDIVIDUAL', sub_type: 'PURCHASER', registration_no: '900202-10-5544', contact_no: '013-000 0000', is_mine: true, creator_name: 'You' },
    ];
  }
}
resetFixtures();

/**
 * Wind a block of the lesson back to its own starting conditions.
 *
 * Going BACK into the middle of a multi-step block lands the agent on a screen
 * whose state no longer exists — /forms/new is always a fresh form, so the
 * parties they created are gone. The tour restarts such a block from its first
 * step, and the sandbox has to match: the contacts must be gone too, or the
 * "your list is empty, so make a new one" step would be a lie.
 */
export function resetBlock(name) {
  if (!S) resetFixtures();
  if (name === 'form-create') {
    S.contacts = [];
    S.otpSigned = [];
    S.otpStatus = 'DRAFT';
    // The screen is rebuilt empty, so the form is unwritten again — otherwise
    // the save step's gate is already satisfied when the agent arrives and the
    // tour bounces straight past the step it sent them back to redo.
    S.formCreated = false;
  }
  if (name === 'invoice-view' || name === 'receipt-sheet') {
    // Nothing sandbox-side to undo: the viewer and the receipt sheet are both
    // component state, and the block exists only so Back re-opens them.
    if (name === 'receipt-sheet') S.receipts = [];
  }
  if (name === 'cs-accept') {
    // Backing into the accept sheet means answering the request again.
    S.csStatus = 'PENDING';
    S.csSplit = 50;
    S.csSaleCreated = false;
  }
  if (name === 'cs-sale') {
    // The sale form is rebuilt empty, so the sale is unraised again.
    S.csSaleCreated = false;
    S.csStatus = 'ACCEPTED';
  }
  if (name === 'cs-form-sign') {
    S.csFormSigned = [];
    if (S.csForm === 'SIGNED') S.csForm = 'PENDING_SIGNING';
  }
  if (name === 'chain-listing') {
    // /listings/new is rebuilt empty, so the listing is unmade again. Without
    // this the publish step's gate is already satisfied on arrival and the tour
    // bounces straight past the step it sent the agent back to redo.
    S.listingCreated = false;
    S.listingPublished = false;
    S.listingStatus = 'ACTIVE';
  }
  if (name === 'chain-form') {
    S.formCreated = false;
  }
  if (name === 'stage-sheet') {
    S.saleStage = 'DRAFT';
    S.saleStageLabel = 'Draft';
  }
  if (name === 'form-sign') {
    S.otpSigned = [];
    S.otpStatus = 'DRAFT';
    S.listingStatus = 'ACTIVE';
  }
}

/**
 * Which branch of the lesson's fork is being walked. The sandbox needs it because
 * the two routes genuinely differ in what issuing does: a form generated from a
 * listing books that listing, a form written from scratch is linked to nothing.
 */
export function setDemoPath(path) {
  if (!S) resetFixtures();
  S.fromListing = path !== 'direct';
}

/** Read-only view of the sandbox, so a step can wait on a real outcome. */
export function getState() { if (!S) resetFixtures(); return { ...S }; }

const OTP_PARTIES = ['purchaser_1', 'owner_1'];

/**
 * A genuine one-page PDF for the demo invoice. The viewer fetches the blob and
 * hands it to react-pdf, so a placeholder object would render an error rather
 * than a document — and "download and send it to your client" is the step.
 */
function demoInvoicePdf() {
  const pdf = [
    '%PDF-1.4',
    '1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj',
    '2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj',
    '3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 420 260]/Resources<</Font<</F1 4 0 R>>>>/Contents 5 0 R>>endobj',
    '4 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold>>endobj',
    '5 0 obj<</Length 210>>stream',
    'BT /F1 16 Tf 30 210 Td (CID REALTORS — DEMO INVOICE) Tj ET',
    'BT /F1 11 Tf 30 180 Td (INV-DEMO-01  ·  SS-DEMO-01) Tj ET',
    'BT /F1 11 Tf 30 158 Td (Residensi Amanina, A-12-03) Tj ET',
    'BT /F1 13 Tf 30 120 Td (Professional fee: RM 13,600.00) Tj ET',
    'BT /F1 10 Tf 30 60 Td (PRACTICE DOCUMENT - NOT A REAL INVOICE) Tj ET',
    'endstream endobj',
    'trailer<</Root 1 0 R>>',
    '%%EOF',
  ].join('\n');
  return new Blob([pdf], { type: 'application/pdf' });
}

/** The payment voucher HQ attaches to the demo payout — a real PDF, for the same
 *  reason as the invoice: the viewer hands the blob to react-pdf. */
function demoVoucherPdf() {
  const pdf = [
    '%PDF-1.4',
    '1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj',
    '2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj',
    '3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 420 260]/Resources<</Font<</F1 4 0 R>>>>/Contents 5 0 R>>endobj',
    '4 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold>>endobj',
    '5 0 obj<</Length 230>>stream',
    'BT /F1 16 Tf 30 210 Td (CID REALTORS - PAYMENT VOUCHER) Tj ET',
    'BT /F1 11 Tf 30 180 Td (PV-DEMO-01  -  SS-DEMO-01) Tj ET',
    'BT /F1 11 Tf 30 158 Td (Residensi Amanina, A-12-03) Tj ET',
    `BT /F1 13 Tf 30 120 Td (Paid to you: ${RMF(MY().share)}) Tj ET`,
    'BT /F1 10 Tf 30 60 Td (PRACTICE DOCUMENT - NOT A REAL VOUCHER) Tj ET',
    'endstream endobj',
    'trailer<</Root 1 0 R>>',
    '%%EOF',
  ].join('\n');
  return new Blob([pdf], { type: 'application/pdf' });
}

/** The demo payout, once the payout beat has played — the server's row shape. */
const DEMO_PAYOUT = () => ({
  id: 'demo-payout-0001',
  payout_date: '2026-08-29',
  amount: MY().share,
  clawback_offset: 0,
  net: MY().share,
  status: 'ACTIVE',
  reversed_at: null,
  channel: 'PBB 000123',
  voucher: { id: 'demo-pv-0001', doc_no: 'PV-DEMO-01', has_file: true, voided: false },
});

/** The bank-in slip the demo sale already carries, so the receipt form can LINK
 *  one instead of uploading — a real upload PUTs to storage, which the sandbox
 *  refuses by design. */
const DEMO_SLIP = {
  id: 'demo-att-slip',
  file_name: 'bank-in-slip-demo.jpg',
  category: 'BANK_IN_SLIP',
  file_url: null,
  uploaded_at: '2026-08-16T02:00:00.000Z',
  uploaded_by_type: 'AGENT',
  co_share_form_id: null,
};

/** Did the agent create a party of this registry type during the lesson? */
export const createdContact = (type) => (getState().contacts || []).some((c) => c.type === type);

/** Called by the tour when an HQ beat plays, so the real pages re-render. */
export function applyBeat(beat) {
  if (!S) resetFixtures();
  if (beat === 'invoice') {
    S.saleStatus = 'Converted';
    S.invoiceRaised = true;
    S.commissionChip = 'Locked';
  }
  // The other REN's side of the lesson, which the agent cannot perform themselves.
  if (beat === 'peer-asks') S.csStatus = 'PENDING';
  if (beat === 'peer-signs') {
    if (!S.csFormSigned.includes(PEER.id)) S.csFormSigned.push(PEER.id);
    if (S.csFormSigned.length >= 2) S.csForm = 'SIGNED';
  }
  if (beat === 'release') S.commissionChip = 'Released';
  if (beat === 'payout') { S.commissionChip = 'Paid'; S.paid = true; }
}

const agent = () => {
  try { return JSON.parse(localStorage.getItem('cid_agent_user') || 'null'); } catch { return null; }
};
const AGENT = () => {
  const a = agent() || {};
  return {
    id: a.id || 'demo-agent',
    full_name: a.full_name || 'Agent',
    display_name: a.display_name || a.full_name || 'Agent',
    ren_no: a.ren_no || '00000',
    ren_status: 'REGISTERED',
    gci_tier: 70,
    management_rank: a.management_rank || 'AGENT',
    branch_id: a.branch_id || 'demo-branch',
  };
};

const LISTING_ROW = () => ({
  id: ID.listing,
  listing_ref: 'PL-DEMO-01',
  property_title: 'Residensi Amanina A-12-03 (demo)',
  property_address: DEAL.address,
  area: DEAL.area,
  state: DEAL.state,
  postcode: DEAL.postcode,
  property_type: 'Condominium',
  property_tenure: 'Freehold',
  listing_type: 'SUBSALE',
  status: S.listingStatus,
  visibility: S.listingVisibility,
  asking_price_rm: DEAL.price,
  price_negotiable: true,
  bedrooms: 3,
  bathrooms: 2,
  car_parks: 2,
  built_up_sqft: 1050,
  land_area_sqft: null,
  expired_at: null,
  cover_image_url: '',
  image_count: 0,
});

const LISTING_DETAIL = () => ({
  ...LISTING_ROW(),
  is_mine: true,
  read_only: false,
  furnish: 'PARTIALLY_FURNISHED',
  facing: 'North-East',
  floor_level: '12',
  description: 'Corner unit, renovated kitchen, two side-by-side car parks. This is a practice listing.',
  features: { gated_guarded: true, swimming_pool: true, gymnasium: true },
  published_at: '2026-08-10T02:00:00.000Z',
  owner_name: DEAL.owner,
  owner_phone: '012-000 0000',
  owner_email: null,
  images: [],
  agents: [{ agent_id: AGENT().id, name: AGENT().full_name, is_primary: true }],
  // Deliberately NOT linked to an ALS. A letter-backed listing hands the OTP a
  // locked, pre-filled owner — which is the easy path, but it means the agent
  // never learns how to add a party. With no letter behind it, both the purchaser
  // and the owner start empty and the lesson can walk the real create flow.
  // The spine's listing has NO letter on purpose (so its OTP starts with empty
  // parties and the agent learns to add them). The chain lesson is the opposite
  // case: the letter is the point.
  authorization_form: S.mode === 'auth'
    ? { id: ID.als, form_ref: 'ALS-DEMO-01', status: 'Issued', sale_type: 'SALES', form_data: ALS_DATA() }
    : null,
  co_share: null,
});

/**
 * The signed authority to sell, as form_data. These exact keys are what the two
 * conversions carry forward — ListingFormPage reads property_address / owner_1_name
 * / selling_price_rm, and FormCreatePage's authFormPrefill reads the owner's
 * identity and the whole payment ladder — so the lesson's "it came from the
 * letter" claim is literally true rather than staged.
 */
const ALS_DATA = () => ({
  date: '2026-08-10',
  property_address: DEAL.address,
  owner_1_id: 'demo-contact-v',
  owner_1_name: DEAL.owner,
  owner_1_nric: '640909-10-5099',
  selling_price_rm: DEAL.price,
  earnest_deposit_percent: 3,
  balance_deposit_percent: 7,
  balance_purchase_price_percent: 90,
  balance_month: 3,
  grace_period: 1,
  interest_percent: 8,
  professional_fee_type: '2% exclusive of 8% SST',
  vacant_possession: true,
  owner_capacity: 'REGISTERED_OWNER',
  appointment_basis: 'EXCLUSIVE',
  appointment_period: 3,
  period_type: 'MONTHS',
  commencing_from: '2026-08-10',
  commencing_to: '2026-11-09',
});

const OTP_LABEL = { DRAFT: 'Draft', PENDING_SIGNING: 'Pending signing', SIGNED: 'Signed', APPROVED: 'Issued' };

const FORM_ROW = (kind) => (kind === 'als'
  ? { id: ID.als, form_ref: 'ALS-DEMO-01', form_type: 'ALS', sale_type: 'SALES', status: 'Issued', status_raw: 'APPROVED', property: DEAL.property }
  : { id: ID.otp, form_ref: 'OTP-DEMO-01', form_type: 'OTP', sale_type: 'SALES', status: OTP_LABEL[S.otpStatus], status_raw: S.otpStatus, property: DEAL.property });

const FORM_DETAIL = (kind) => {
  const base = FORM_ROW(kind);
  const isOtp = kind === 'otp';
  return {
    ...base,
    branch_id: AGENT().branch_id,
    listing_id: isOtp ? ID.listing : null,
    sale_id: null,
    issuable: isOtp && ['DRAFT', 'PENDING_SIGNING', 'SIGNED'].includes(S.otpStatus),
    convertible: false,
    // The letter can become a listing only while it has not already made one.
    listing_convertible: kind === 'als' && S.mode === 'auth' && !S.listingCreated,
    generated_listing: kind === 'als' && !(S.mode === 'auth' && !S.listingCreated)
      ? { id: ID.listing, listing_ref: 'PL-DEMO-01' } : null,
    listing_warning: null,
    pdf_url: null,
    // The ALS carries its REAL contents in the chain lesson: both conversions
    // read these very keys, so "it came from the letter" is demonstrable rather
    // than narrated.
    form_data: kind === 'als' && S.mode === 'auth' ? ALS_DATA() : {
      property_address: DEAL.address,
      owner_1_name: DEAL.owner,
      purchaser_1_name: DEAL.buyer,
      selling_price_rm: DEAL.price,
    },
    signatories: isOtp
      ? [
        { key: 'purchaser_1', role: 'Purchaser (1)', name: DEAL.buyer, nric: '920304-10-5566', is_witness: false, status: S.otpSigned.includes('purchaser_1') ? 'SIGNED' : 'NOT_STARTED', state: S.otpSigned.includes('purchaser_1') ? 'signed' : 'not_started', at: S.otpSigned.includes('purchaser_1') ? 'just now' : null, email: null, signing_method: 'ON_DEVICE' },
        { key: 'owner_1', role: 'Owner (1)', name: DEAL.owner, nric: '780512-14-5501', is_witness: false, status: S.otpSigned.includes('owner_1') ? 'SIGNED' : 'NOT_STARTED', state: S.otpSigned.includes('owner_1') ? 'signed' : 'not_started', at: S.otpSigned.includes('owner_1') ? 'just now' : null, email: null, signing_method: 'ON_DEVICE' },
      ]
      : [
        { key: 'owner_1', role: 'Owner 1', name: DEAL.owner, nric: '780512-14-5501', is_witness: false, status: 'SIGNED', state: 'signed', at: 'today', email: null, signing_method: 'ON_DEVICE' },
      ],
    witnesses: [],
    // BARE numbers, exactly as bookingFormTemplate._computeFormatted returns them
    // (formatCurrency emits "20,400.00", never "RM 20,400.00"). Both builds add
    // the RM themselves when they print "3% · RM 20,400.00", so a currency symbol
    // here comes out doubled on the screen the chain lesson points at.
    formatted: {
      selling_price: '680,000.00',
      earnest_deposit: '20,400.00',
      balance_deposit: '47,600.00',
      balance: '612,000.00',
    },
    linked_listing: isOtp ? { id: ID.listing, listing_ref: 'PL-DEMO-01', property_address: DEAL.address, status: S.listingStatus } : null,
    linked_sale: null,
  };
};

const SALE_ROW = () => ({
  id: ID.sale,
  ref: 'SS-DEMO-01',
  sale_ref: 'SS-DEMO-01',
  title: DEAL.property,
  type: 'Subsales',
  deal_type: 'SUBSALES',
  status: S.saleStatus,
  stage: S.saleStage,
  stage_raw: S.saleStage,
  stageLabel: S.saleStageLabel,
  clabel: S.saleStatus,
  priceF: 'RM 680,000.00',
  vendor: DEAL.owner,
  purchaser: DEAL.buyer,
  closed: '—',
});

const SALE_DETAIL = () => ({
  ...SALE_ROW(),
  // A Converted sale still opens its figures ('invoiced'); 'limited' is only for
  // ERP-imported ledgers, which a lesson's sale never is.
  edit_level: S.saleStatus === 'Pending' ? 'full' : 'invoiced',
  commission_hand_set: false,
  editable: S.saleStatus === 'Pending',
  // Verbatim copy of AGENT_SETTABLE_STAGES on the server — both stage pickers
  // intersect their own render list against this, so a value missing here means
  // the lesson's anchor never materialises and the step hangs.
  settable_stages: ['DRAFT', 'BALANCE_TO', 'REQUEST_INVOICE', 'REQUEST_RECEIPT', 'READY_TO_CLAIM', 'CANCEL_REFUND_LOAN_REJECT'],
  lock: {
    locked: S.saleStatus !== 'Pending',
    can_add_receipt: true,
    hint: S.saleStatus === 'Pending'
      ? 'Your figures are an estimate until HQ raises the invoice.'
      : 'Commission is calculated and the figures are locked.',
  },
  danger: { can_cancel: false, cancel_blocked_reason: 'Not available in a practice lesson.', can_delete: false, delete_blocked_reason: 'Not available in a practice lesson.', booking_form: null },
  financials: {
    sale_date: '2026-08-15',
    gross_sales_amount: DEAL.price,
    commission_percentage: 2,
    total_commission: DEAL.fee,
    vendor_pay_tax: true,
    sst_rate: 8,
    sst_amount: 1088,
    net_gci: DEAL.fee,
  },
  parties: {
    vendor: DEAL.owner,
    purchaser: DEAL.buyer,
    coAgency: '—',
    // A co-shared sale carries BOTH RENs, which is what makes the internal
    // co-share form applicable at all (the server gates it on agent_count > 1).
    agents: S.mode === 'coshare'
      ? [
        { name: AGENT().full_name, ren: AGENT().ren_no, split_percentage: 100 - S.csSplit, self: true },
        { name: PEER.name, ren: PEER.ren, split_percentage: S.csSplit, self: false },
      ]
      : [{ name: AGENT().full_name, ren: AGENT().ren_no, split_percentage: 100, self: true }],
    coAgencies: [],
  },
  commissions: [{
    id: 'demo-comm-1',
    kind: 'Personal commission',
    commission_type: 'PERSONAL_COMMISSION',
    chip: S.commissionChip,
    amount: RMF(MY().share),
    unlocked: S.commissionChip === 'Locked' ? 'RM 0.00' : RMF(MY().share),
    paid: S.paid ? RMF(MY().share) : 'RM 0.00',
    amount_n: MY().share,
    unlocked_n: S.commissionChip === 'Locked' ? 0 : MY().share,
    paid_n: S.paid ? MY().share : 0,
  }],
  receipts: S.receipts,
  atts: [],
  comments: [],
});

const NOTIFS = () => [
  { id: 'demo-n1', type: 'money', category: 'sales', title: 'Invoice issued on SS-DEMO-01', body: 'Your commission figures are locked from here.', deep_link: `/sales/${ID.sale}`, read_at: null, created_at: new Date().toISOString(), when: 'just now' },
];

/* ── the co-share lesson's records ───────────────────────────────────────── */

const CS_REQUEST = () => {
  const accepted = S.csStatus !== 'PENDING';
  return {
    id: ID.csr,
    status: S.csStatus,
    // The agent is the LISTER here — it is their listing, and answering is the
    // whole first half of the lesson.
    is_lister: true,
    requester: { id: PEER.id, name: PEER.name, ren_no: PEER.ren, branch_name: PEER.branch, phone: '012-555 5555' },
    lister: { id: AGENT().id, name: AGENT().full_name, ren_no: AGENT().ren_no },
    listing: {
      id: ID.listing, listing_ref: 'PL-DEMO-01', property_title: LISTING_ROW().property_title,
      property_address: DEAL.address, asking_price_rm: DEAL.price, listing_type: 'SUBSALE',
      area: DEAL.area, state: DEAL.state, cover_image_url: '',
    },
    proposed_split_percentage: 50,
    agreed_split_percentage: accepted ? S.csSplit : null,
    message: 'I have a buyer who viewed a similar unit upstairs last week and wants to see A-12-03 this Saturday. Happy to split it evenly if it closes.',
    response_note: accepted ? 'Yes — bring them Saturday 3pm.' : null,
    can_respond: S.csStatus === 'PENDING',
    can_create_sale: S.csStatus === 'ACCEPTED' && !S.csSaleCreated,
    can_withdraw: S.csStatus !== 'CONVERTED',
    sale_id: S.csSaleCreated ? ID.sale : null,
    sale_ref: S.csSaleCreated ? 'SS-DEMO-01' : null,
    created_at: '2026-08-16T01:30:00.000Z',
    when: 'yesterday',
  };
};

const CS_FORM = () => {
  if (!S.csForm) return null;
  const mineSigned = S.csFormSigned.includes(AGENT().id);
  const peerSigned = S.csFormSigned.includes(PEER.id);
  const signedCount = S.csFormSigned.length;
  return {
    id: ID.csform,
    form_ref: 'ICS-DEMO-01',
    version: 1,
    status: S.csForm,
    sale_id: ID.sale,
    sale_ref: 'SS-DEMO-01',
    deal_type: 'SUBSALES',
    property_address: DEAL.address,
    gross_amount_rm: DEAL.price,
    total_commission: DEAL.fee,
    // The practice deal bills SST to the vendor on top, so the RENs share the
    // whole professional fee and there is no subtraction to show.
    vendor_pay_tax: true,
    sst_rate: 8,
    sst_amount_rm: 1088,
    net_fee_rm: DEAL.fee,
    signatories: [
      { id: 'sig-me', agent_id: AGENT().id, agent_name: AGENT().full_name, ren_no: AGENT().ren_no, share_percentage: 100 - S.csSplit, signed: mineSigned, signed_at: mineSigned ? '2026-08-17T04:00:00.000Z' : null, is_me: true },
      { id: 'sig-peer', agent_id: PEER.id, agent_name: PEER.name, ren_no: PEER.ren, share_percentage: S.csSplit, signed: peerSigned, signed_at: peerSigned ? '2026-08-17T05:00:00.000Z' : null, is_me: false },
    ],
    signatory_count: 2,
    signed_count: signedCount,
    my_signature_pending: S.csForm === 'PENDING_SIGNING' && !mineSigned,
    can_issue: S.csForm === 'DRAFT',
    can_sign: S.csForm === 'PENDING_SIGNING' && !mineSigned,
    can_void: S.csForm !== 'SIGNED',
    drift: { stale: false, reason: null, changes: [] },
    branch_name: PEER.branch,
  };
};

/** The fee-split agreement as the agent reads it before signing. */
const CS_FORM_HTML = () => `
  <div style="font-family:system-ui;padding:22px;line-height:1.6">
    <div style="font-size:11px;letter-spacing:.12em;color:#888">CID REALTORS SDN BHD · PRACTICE DOCUMENT</div>
    <h2 style="margin:6px 0 2px">Internal Co-Share Agreement</h2>
    <div style="font-family:ui-monospace,monospace;font-size:12px;color:#666">ICS-DEMO-01 · SS-DEMO-01</div>
    <hr style="margin:14px 0;border:none;border-top:1px solid #ddd"/>
    <p><b>Property:</b> ${DEAL.address}</p>
    <p><b>Sale price:</b> ${RMF(DEAL.price)}<br/><b>Professional fee shared:</b> ${RMF(DEAL.fee)}</p>
    <p><b>The split</b></p>
    <table style="width:100%;border-collapse:collapse;font-size:13px">
      <tr><td style="padding:6px 0">${AGENT().full_name} (${AGENT().ren_no})</td><td style="text-align:right"><b>${100 - S.csSplit}%</b></td></tr>
      <tr><td style="padding:6px 0">${PEER.name} (${PEER.ren})</td><td style="text-align:right"><b>${S.csSplit}%</b></td></tr>
    </table>
    <p style="font-size:12px;color:#666">Clause 5 — any variation of these terms must be agreed in writing and signed again by both RENs.</p>
    <p style="margin-top:22px;font-size:11px;color:#b45309">PRACTICE DOCUMENT — NOT A REAL AGREEMENT</p>
  </div>`;

/**
 * The booking form, as a document.
 *
 * The DESKTOP build renders the letter beside the fields and re-renders it on
 * every keystroke (POST /booking-forms/preview), and shows the saved one beside
 * the record (GET /booking-forms/:id/preview). Neither endpoint exists on the
 * phone, which hides the document behind a button — so without these the desktop
 * lesson would run with an error where the document should be, on the very
 * screen whose whole shape is that document.
 *
 * It reads the SUBMITTED form_data rather than the fixture's, because the step
 * that points at the preview promises the agent that what they type is what will
 * be signed. A canned document would make that sentence a lie the moment they
 * changed a figure.
 */
const DOC_ROW = (label, value) => (value === undefined || value === null || value === ''
  ? ''
  : `<tr><td style="padding:5px 0;color:#666">${label}</td><td style="padding:5px 0;text-align:right"><b>${value}</b></td></tr>`);

const CODE_TITLE = {
  OTP: 'Offer to Purchase', OTR: 'Offer to Rent',
  ALS: 'Authorisation to Sell', ALR: 'Authorisation to Let',
};

const BOOKING_HTML = (code, fd = {}, ref = 'PREVIEW') => {
  const isRent = code === 'OTR' || code === 'ALR';
  const buyerSide = isRent ? fd.tenant_1_name : fd.purchaser_1_name;
  const ownerSide = isRent ? fd.landlord_1_name : fd.owner_1_name;
  const money = (v) => (Number.isFinite(Number(v)) && String(v).trim() !== '' ? RMF(Number(v)) : v);
  return `
  <div style="font-family:system-ui;padding:26px;line-height:1.65;color:#111">
    <div style="font-size:11px;letter-spacing:.12em;color:#888">CID REALTORS SDN BHD · PRACTICE DOCUMENT</div>
    <h2 style="margin:6px 0 2px">${CODE_TITLE[code] || 'Booking Form'}</h2>
    <div style="font-family:ui-monospace,monospace;font-size:12px;color:#666">${ref} · ${code || '—'}</div>
    <hr style="margin:16px 0;border:none;border-top:1px solid #ddd"/>
    <p><b>Property</b><br/>${fd.property_address || '<span style="color:#bbb">— address not filled in yet —</span>'}</p>
    <p>
      <b>${isRent ? 'Landlord' : 'Owner / Vendor'}</b><br/>${ownerSide || '<span style="color:#bbb">— not named yet —</span>'}
      ${code === 'OTP' || code === 'OTR'
        ? `<br/><br/><b>${isRent ? 'Tenant' : 'Purchaser'}</b><br/>${buyerSide || '<span style="color:#bbb">— not named yet —</span>'}`
        : ''}
    </p>
    <table style="width:100%;border-collapse:collapse;font-size:13px;margin-top:8px">
      ${DOC_ROW(isRent ? 'Monthly rental' : 'Selling price', money(fd.selling_price_rm ?? fd.rental_rm))}
      ${DOC_ROW('Earnest deposit', fd.earnest_deposit_percent != null ? `${fd.earnest_deposit_percent}%` : undefined)}
      ${DOC_ROW('Balance deposit', fd.balance_deposit_percent != null ? `${fd.balance_deposit_percent}%` : undefined)}
      ${DOC_ROW('Balance purchase price', fd.balance_purchase_price_percent != null ? `${fd.balance_purchase_price_percent}%` : undefined)}
      ${DOC_ROW('Balance payable within', fd.balance_month != null ? `${fd.balance_month} month(s)` : undefined)}
      ${DOC_ROW('Grace period', fd.grace_period != null ? `${fd.grace_period} month(s)` : undefined)}
      ${DOC_ROW('Interest on late payment', fd.interest_percent != null ? `${fd.interest_percent}% p.a.` : undefined)}
      ${DOC_ROW('Stakeholder', fd.stakeholder)}
      ${DOC_ROW('Professional fee', fd.professional_fee_type)}
    </table>
    ${fd.other_terms ? `<p style="margin-top:14px"><b>Other conditions</b><br/>${fd.other_terms}</p>` : ''}
    <table style="width:100%;margin-top:34px;font-size:12px;color:#666">
      <tr>
        <td style="border-top:1px solid #999;padding-top:6px;width:45%">${ownerSide || '&nbsp;'}</td>
        <td style="width:10%"></td>
        <td style="border-top:1px solid #999;padding-top:6px;width:45%">${buyerSide || '&nbsp;'}</td>
      </tr>
    </table>
    <p style="margin-top:22px;font-size:11px;color:#b45309">PRACTICE DOCUMENT — NOT A REAL AGREEMENT</p>
  </div>`;
};

/* ── route table ─────────────────────────────────────────────────────────── */
const page = (rows) => ({ data: rows, paginated: true, pagination: { page: 1, limit: 20, total: rows.length, totalPages: 1 } });
const one = (data) => ({ data });

export function resolve(method, path, config) {
  if (!S) resetFixtures();
  const p = path.replace(/^.*\/api\/agent/, '');
  const params = config?.params || {};

  if (method === 'GET') {
    if (p === '/me') return one(AGENT());
    if (p === '/me/preferences') return one({ theme: 'system', bioOn: true, leaderboardOn: true });
    if (p === '/me/documents') return one([]);
    if (p === '/home/summary') {
      return one({
        agent: AGENT(),
        gci: { collected: MY().net, ytd: MY().net, lifetime: MY().net },
        tier: { current: 70, next: 75, basis: 'lifetime', belowBand: false, floor: 50000, target: 100000, progress: MY().net, progressPct: 13.6, toNext: 86400 },
        money: { entitled: MY().share, locked: S.commissionChip === 'Locked' ? MY().share : 0, released: S.commissionChip === 'Released' ? MY().share : 0, paid: S.paid ? MY().share : 0, paidThisYear: S.paid ? MY().share : 0, hasLedger: true },
        closingsThisMonth: 1,
        leaderboard: { rank: 1, of: 1, ranked: false },
        needsYou: [{ kind: 'form', tone: 'accent', text: 'OTP-DEMO-01 is ready to issue', sub: DEAL.property, to: `/forms/${ID.otp}` }],
        needsYouPreview: 3,
        needsYouTotal: 1,
        badges: { coShare: 0 },
        recent: [],
      });
    }
    if (p === '/listings') {
      if (params.scope === 'others') return page([]);
      // In the chain lesson there is nothing to list until the letter has been
      // converted — which is the step the whole lesson turns on.
      if (S.mode === 'auth' && !S.listingCreated) return page([]);
      return page([LISTING_ROW()]);
    }
    if (p === `/listings/${ID.listing}`) return one(LISTING_DETAIL());
    // The listing form's postcode field auto-fills city and state from this. An
    // unmatched GET here degrades to `{}`, which silently fills nothing — and the
    // step that says "the city and state fill themselves in" would be a lie the
    // publish button then catches.
    if (/^\/listings\/postcode-lookup\//.test(p)) {
      return one({ postcode: p.split('/').pop(), state: DEAL.state, city: DEAL.area });
    }
    if (p === '/listings/authorization-forms') {
      if (S.mode !== 'auth' || S.listingCreated) return one([]);
      return one([{
        id: ID.als, form_ref: 'ALS-DEMO-01', status: 'APPROVED', sale_type: 'SALES',
        property_address: DEAL.address, owner_name: DEAL.owner, asking_price_rm: DEAL.price, furnish: null,
      }]);
    }
    if (p === '/booking-forms') {
      // Only the letter exists at the start of the chain lesson; the OTP is
      // written at the end of it.
      if (S.mode === 'auth') return page(S.formCreated ? [FORM_ROW('otp'), FORM_ROW('als')] : [FORM_ROW('als')]);
      return page([FORM_ROW('otp'), FORM_ROW('als')]);
    }
    if (p === `/booking-forms/${ID.otp}`) return one(FORM_DETAIL('otp'));
    if (p === `/booking-forms/${ID.als}`) return one(FORM_DETAIL('als'));
    // The saved document, rendered — the desktop detail screen shows it beside
    // the record rather than behind a button.
    if (/^\/booking-forms\/[^/]+\/preview$/.test(p)) {
      const kind = p.includes(ID.als) ? 'als' : 'otp';
      const d = FORM_DETAIL(kind);
      return one({ html: BOOKING_HTML(d.form_type, d.form_data, d.form_ref) });
    }
    if (p === '/sales') return page([SALE_ROW()]);
    if (p === `/sales/${ID.sale}`) return one(SALE_DETAIL());
    if (p === `/sales/${ID.sale}/attachments`) return one([DEMO_SLIP]);
    if (p === `/sales/${ID.sale}/notes/unread-count`) return one({ count: 0 });
    if (/\/sales\/[^/]+\/notes$/.test(p)) return page([]);
    if (/\/sales\/[^/]+\/finance$/.test(p)) {
      /* Shaped EXACTLY like sale.service.getFinance: plain numbers, and the keys
         the server actually sends. Both builds format these themselves — the
         phone with RM(), the desktop table with RM() in a column — so a
         pre-formatted string here comes out as "RM NaN", and a missing `has_pdf`
         hides the View PDF button the money chapter tells the agent to press.
         The sandbox has to speak the server's contract, not a near-miss of it. */
      const sst = round2(DEAL.fee * 0.08);
      const invTotal = round2(DEAL.fee + sst);
      const mine = MY().share;
      const unlocked = S.commissionChip === 'Locked' ? 0 : mine;
      return one({
        rollup: {
          net_gci: DEAL.fee,
          total_commission: DEAL.fee,
          received: DEAL.earnest,
          held_balance: DEAL.earnest,
          invoiced: S.invoiceRaised ? invTotal : 0,
          invoice_outstanding: S.invoiceRaised ? invTotal : 0,
          my: { entitled: mine, unlocked, paid: S.paid ? mine : 0 },
        },
        client_ac: { balance: DEAL.earnest, breakdown: [] },
        invoices: S.invoiceRaised ? [{
          id: 'demo-inv-1',
          doc_no: 'INV-DEMO-01',
          source: 'SQL',
          doc_date: '2026-08-17',
          counterparty: DEAL.owner,
          counterparty_type: 'Vendor',
          subtotal: DEAL.fee,
          sst_amount: sst,
          total: invTotal,
          applied: 0,
          outstanding: invTotal,
          status: 'ISSUED',
          status_label: 'Issued',
          issued: true,
          // The sandbox serves the bytes too — see the /invoices/:id/pdf route
          // below. Without this flag neither build renders the button that
          // reaches them, and the step that says "open it" has nothing to point at.
          has_pdf: true,
        }] : [],
        refunds: [],
        cash_movements: [],
      });
    }
    if (p === '/earnings/summary') {
      const rel = S.commissionChip === 'Locked' ? 0 : MY().share;
      const pd = S.paid ? MY().share : 0;
      return one({ agent: AGENT(), tier: 70, ytdGci: MY().net, lifetimeGci: MY().net, nextTier: 75, toNextTier: 86400, entitled: MY().share, released: rel, pending: Math.max(0, rel - pd), paid: pd });
    }
    if (p === '/earnings/summary/period') {
      // PENDING is released-but-not-yet-paid — NOT "everything unpaid". While the
      // commission is still Locked nothing is pending, which is the whole point of
      // the release beat: the figure moves Locked → Pending → Paid.
      const released = S.commissionChip === 'Locked' ? 0 : MY().share;
      const paid = S.paid ? MY().share : 0;
      // `released` and `payouts` are what the tiles' SUB-LINES quote — "of RM X
      // released", "RM X disbursed". Left out, both read RM 0 while the figure
      // above them moves, which is the opposite of what the release beat is
      // showing the agent. Same keys earnings.service.getPeriodSummary returns.
      return one({
        earned: MY().share,
        released,
        paid,
        pending: Math.max(0, released - paid),
        personal: MY().share,
        override: 0,
        deals: 1,
        payouts: paid,
      });
    }
    if (p === '/earnings/monthly') {
      // The practice deal is one sale in the demo month (its sale_date, Aug 2026),
      // so the chart shows a single month standing alone — everything the lesson's
      // agent has earned. Same keys and same basis as earnings.service.getMonthly.
      const DEMO_MONTH = 8;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        .map((label, i) => {
          const mine = i + 1 === DEMO_MONTH ? MY().share : 0;
          return { month: i + 1, label, commission: mine, override: 0, total: mine, deals: mine ? 1 : 0 };
        });
      return one({
        year: 2026,
        years: [2026],
        months,
        totals: { commission: MY().share, override: 0, total: MY().share, deals: 1 },
      });
    }
    if (p === '/earnings/by-sale') {
      // entitled / unlocked / paid are what the CLIENT reads — lib/commission
      // stateLine() derives the row's prose from these three numbers, so a row
      // that only carried a formatted string read "Nothing released yet" next to
      // a Released chip.
      const unlocked = S.commissionChip === 'Locked' ? 0 : MY().share;
      return page([{
        id: 'demo-comm-1', sale_id: ID.sale, sale_ref: 'SS-DEMO-01',
        property: DEAL.property, property_address: DEAL.property,
        commission_type: 'PERSONAL_COMMISSION',
        chip: S.commissionChip,
        amount: RMF(MY().share),
        entitled: MY().share,
        unlocked,
        paid: S.paid ? MY().share : 0,
      }]);
    }
    if (p === '/earnings/overrides') return page([]);
    if (p === '/earnings/ledger') {
      // earnings.service.getLedger's own keys and types: `remaining_balance`,
      // not `balance`, and plain numbers. Both builds run these through RM(), so
      // a formatted string here printed "RM NaN" in the payout ledger panel.
      const paid = S.paid ? MY().share : 0;
      const released = S.commissionChip === 'Locked' ? 0 : MY().share;
      return one({
        remaining_balance: Math.max(0, round2(released - paid)),
        total_due: released,
        total_paid: paid,
        total_clawback: 0,
        status: paid > 0 ? 'PAID' : 'PENDING',
      });
    }
    // The payout the HQ beat makes, with the voucher it attaches. Before that
    // beat there is no payout, so the list is honestly empty.
    if (p === '/earnings/payouts') return page(S.paid ? [DEMO_PAYOUT()] : []);
    if (p === '/earnings/payouts/demo-payout-0001') {
      return one({
        ...DEMO_PAYOUT(),
        reversal_reason: null,
        lines: [{ sale_id: ID.sale, sale_ref: 'SS-DEMO-01', property: DEAL.property, commission_type: 'PERSONAL_COMMISSION', amount: MY().share }],
      });
    }
    // The viewer fetch()es the link itself, outside axios, so the PDF is handed
    // over as a blob: URL rather than a signed storage link.
    if (p === '/earnings/payouts/demo-payout-0001/voucher') {
      return one({ url: URL.createObjectURL(demoVoucherPdf()), doc_no: 'PV-DEMO-01', file_name: 'PV-DEMO-01.pdf', voided: false });
    }
    if (/\/sales\/[^/]+\/invoices\/[^/]+\/pdf$/.test(p)) return { data: demoInvoicePdf(), raw: true };
    if (p === '/notifications') return page(NOTIFS());
    if (p === '/notifications/unread-count') return one({ count: 1 });
    if (p === '/co-share-requests/summary') {
      const cs = S.mode === 'coshare';
      const pending = cs && S.csStatus === 'PENDING' ? 1 : 0;
      const accepted = cs && S.csStatus !== 'PENDING' ? 1 : 0;
      return one({ incoming_pending: pending, incoming_accepted: accepted, outgoing_pending: 0, outgoing_accepted: 0, actionable: pending });
    }
    if (p === '/co-share-requests') {
      // Only the INCOMING box has anything: the agent holds the listing in this
      // lesson, so there is nothing they have asked for.
      if (S.mode !== 'coshare' || params.box === 'outgoing') return page([]);
      return page([CS_REQUEST()]);
    }
    if (p === `/co-share-requests/${ID.csr}`) return one(CS_REQUEST());
    if (/\/sales\/[^/]+\/co-share-form$/.test(p)) {
      // `applicable` is false on a single-agent sale — the spine's deal — so the
      // row does not even offer itself there.
      const cs = S.mode === 'coshare';
      return one({
        applicable: cs,
        agent_count: cs ? 2 : 1,
        shares: cs ? [100 - S.csSplit, S.csSplit] : [100],
        form: cs ? CS_FORM() : null,
        history: [],
      });
    }
    if (p === `/co-share-forms/${ID.csform}`) return one(CS_FORM());
    if (p === `/co-share-forms/${ID.csform}/preview`) return one({ html: CS_FORM_HTML() });
    if (p === '/co-agency-letters') return page([]);
    if (p === '/contacts') return page([]);
    if (p === '/performance/scopes') return one({ can: { direct: false, allGens: false, branch: false }, counts: { direct: 0, allGens: 0, branch: 0 }, branches: [] });
    // The demo agent has no team, so nothing should ask for one — but Home reads
    // the scopes from a module-level cache that can outlive a real session, and an
    // unmatched GET here degrades to `{}`, which is a shape the team card cannot
    // survive. Served explicitly, and shaped like the server's own reply.
    if (p === '/performance/summary') {
      return one({
        scope: 'direct', branch: null,
        period: { from: null, to: null, label: 'This month' },
        members: { total: 0, producing: 0, dormant: 0, inactive: 0 },
        gci: 0, volume: 0, deals: 0, avgDeal: 0, avgPerMember: 0,
        override: 0, overrideUnattributed: 0, groups: [], pipeline: { gci: 0, deals: 0 },
      });
    }
    if (p === '/performance/members' || p === '/performance/deals') return page([]);
    if (p === '/performance/attention') return one({ items: [] });
    if (p === '/career/tier-ladder') return one({ rungs: [] });
    if (p === '/documents') return page([]);
  }

  // Reads that are harmless to fake, and the two writes a lesson performs. Every
  // other mutation is refused by the adapter, deliberately.
  if (method === 'PUT' && /\/listings\/[^/]+\/visibility$/.test(p)) {
    const body = (() => { try { return JSON.parse(config.data || '{}'); } catch { return {}; } })();
    S.listingVisibility = String(body.visibility || 'PUBLIC').toUpperCase();
    return one(LISTING_DETAIL());
  }
  // The app sends PUT here, not PATCH — matching the wrong verb is what made
  // the stage change silently fail inside a lesson.
  if ((method === 'PUT' || method === 'PATCH') && /\/sales\/[^/]+\/stage$/.test(p)) {
    const body = (() => { try { return JSON.parse(config.data || '{}'); } catch { return {}; } })();
    // A stage missing from this map renders the raw enum string on the sale card.
    const LABEL = { DRAFT: 'Draft', BALANCE_TO: 'Balance', REQUEST_INVOICE: 'Request invoice', REQUEST_RECEIPT: 'Request receipt', READY_TO_CLAIM: 'Ready to claim', CANCEL_REFUND_LOAN_REJECT: 'Cancelled' };
    if (body.stage) { S.saleStage = body.stage; S.saleStageLabel = LABEL[body.stage] || body.stage; }
    return one(SALE_DETAIL());
  }
  // On-device signing. The last required party flips the form to SIGNED, exactly
  // as bookingForm.service does — that transition is what the lesson is teaching.
  if (method === 'POST' && /\/booking-forms\/[^/]+\/on-device-sign$/.test(p)) {
    const body = (() => { try { return JSON.parse(config.data || '{}'); } catch { return {}; } })();
    const key = body.signatory_key;
    if (key && !S.otpSigned.includes(key)) S.otpSigned.push(key);
    const allSigned = OTP_PARTIES.every((k) => S.otpSigned.includes(k));
    S.otpStatus = allSigned ? 'SIGNED' : 'PENDING_SIGNING';
    return one({ ...FORM_DETAIL('otp'), all_signed: allSigned });
  }
  // Issuing is the agent's own call — no approver — and it books the listing.
  if (method === 'POST' && /\/booking-forms\/[^/]+\/issue$/.test(p)) {
    S.otpStatus = 'APPROVED';
    // Only a form that came FROM a listing books one. Written from scratch it is
    // linked to nothing, and moving the practice listing anyway would contradict
    // the step that has just told the agent nothing was booked.
    if (S.fromListing) S.listingStatus = 'UNDER_OFFER';
    return one(FORM_DETAIL('otp'));
  }
  // The party picker's search. NOTE: a FLAT array, no pagination envelope —
  // usePagedOptions reads r.data directly and ends the feed on a short page.
  if (method === 'GET' && p === '/contacts/lookup') {
    const type = String(params.type || '').toUpperCase();
    const q = String(params.q || '').trim().toLowerCase();
    const rows = (S.contacts || [])
      .filter((c) => c.type === type)
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .map((c) => ({
        id: c.id, name: c.name, type: c.type, code: null,
        registration_no: c.registration_no, address: null,
        // A key the picker passes through to its option row, so a step can point
        // at a specific contact inside the sheet.
        tour: `contact-opt-${String(c.type || '').toLowerCase()}`,
        contact_no: c.contact_no || null, is_mine: true, creator_name: 'You',
      }));
    return one(rows);
  }

  // Creating a party. Returns the DETAIL shape (the real create ends with
  // getById), because that is what onQuickCreated reads to fill the form.
  if (method === 'POST' && /\/(purchasers|vendors|co-agencies)$/.test(p)) {
    const body = (() => { try { return JSON.parse(config.data || '{}'); } catch { return {}; } })();
    const type = p.includes('purchasers') ? 'PURCHASER' : p.includes('vendors') ? 'VENDOR' : 'CO_AGENCY';
    const row = {
      id: `demo-contact-${(S.contacts.length + 1)}`,
      name: String(body.name || '').trim(),
      entity_type: body.entity_type || 'INDIVIDUAL',
      sub_type: body.sub_type || type,
      type,
      registration_no: String(body.registration_no || '').trim(),
      contact_no: body.contact_no || null,
      phone: body.contact_no || null,
      address: body.address || null,
      contact_person: body.contact_person || null,
      nationality: body.nationality || 'Malaysian',
      email: null, board_register_no: null,
      bank_accounts: [], linked_deals: [],
      code: null, is_mine: true, creator_name: 'You',
    };
    S.contacts.push(row);
    return one(row);
  }

  // Creating the listing the letter authorised. A listing is always born a
  // private draft — "Publish" is the status call immediately after this one.
  if (method === 'POST' && p === '/listings') {
    S.listingCreated = true;
    S.listingStatus = 'DRAFT';
    return one({ ...LISTING_DETAIL(), id: ID.listing });
  }
  // …and that status call. Without it the sandbox refused the second half of
  // Publish, so the agent got "this action is not available in a practice
  // lesson" on a step whose whole job was to publish.
  if (method === 'PUT' && /\/listings\/[^/]+\/status$/.test(p)) {
    const body = (() => { try { return JSON.parse(config.data || '{}'); } catch { return {}; } })();
    S.listingStatus = String(body.status || 'ACTIVE').toUpperCase();
    S.listingPublished = S.listingStatus === 'ACTIVE';
    return one(LISTING_DETAIL());
  }

  // The live document beside the create form (desktop). A read dressed as a POST
  // — it renders the body and stores nothing — so it is served rather than
  // refused, which is what an unmatched write would be.
  if (method === 'POST' && p === '/booking-forms/preview') {
    const body = JSON.parse(config?.data || '{}');
    return one({ html: BOOKING_HTML(body.form_type, body.form_data || {}, body.form_ref || 'PREVIEW') });
  }

  // Writing the booking form. The app has already enforced its twelve mandatory
  // fields by the time this runs — reaching here IS the proof the agent filled
  // them, which is what the save step waits for.
  if (method === 'POST' && p === '/booking-forms') {
    S.formCreated = true;
    return one({ ...FORM_DETAIL('otp'), id: ID.otp });
  }

  // Answering the co-share. The agreed split is whatever the agent typed in the
  // sheet — everything downstream (the sale's agent rows, the form, the money)
  // reads it back from here, so the lesson's arithmetic is theirs and not ours.
  if (method === 'PATCH' && /\/co-share-requests\/[^/]+\/accept$/.test(p)) {
    const body = (() => { try { return JSON.parse(config.data || '{}'); } catch { return {}; } })();
    const n = Number(body.agreed_split_percentage);
    if (Number.isFinite(n) && n >= 0 && n <= 100) S.csSplit = n;
    S.csStatus = 'ACCEPTED';
    return one(CS_REQUEST());
  }

  // Raising the sale from the accepted request. The real create returns the sale,
  // and the page navigates to it.
  if (method === 'POST' && p === '/sales') {
    S.csSaleCreated = true;
    S.csStatus = 'CONVERTED';
    return one({ ...SALE_DETAIL(), id: ID.sale });
  }

  // Drafting the fee-split agreement off the sale.
  if (method === 'POST' && /\/sales\/[^/]+\/co-share-form$/.test(p)) {
    S.csForm = 'DRAFT';
    S.csFormSigned = [];
    return one(CS_FORM());
  }
  // Issuing freezes the figures and asks the other REN to sign.
  if (method === 'PATCH' && /\/co-share-forms\/[^/]+\/issue$/.test(p)) {
    S.csForm = 'PENDING_SIGNING';
    return one(CS_FORM());
  }
  // A REN signs their OWN row — the server finds it by the caller's id, so the
  // sandbox does the same rather than trusting anything in the body.
  if (method === 'PATCH' && /\/co-share-forms\/[^/]+\/sign$/.test(p)) {
    if (!S.csFormSigned.includes(AGENT().id)) S.csFormSigned.push(AGENT().id);
    if (S.csFormSigned.length >= 2) S.csForm = 'SIGNED';
    return one(CS_FORM());
  }

  // Recording a receipt. It lands PENDING_VALIDATION — inert until finance acts,
  // which is exactly the point the lesson is making.
  if (method === 'POST' && /\/sales\/[^/]+\/receipts$/.test(p)) {
    const body = (() => { try { return JSON.parse(config.data || '{}'); } catch { return {}; } })();
    const amt = Number(body.amount || 0);
    S.receipts.unshift({
      id: `demo-rcpt-${S.receipts.length + 1}`,
      label: 'Earnest deposit',
      amount: `RM ${amt.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      chip: 'Pending',
      status: 'PENDING_VALIDATION',
      payer: body.payer_name || DEAL.buyer,
      receipt_date: body.receipt_date || '2026-08-17',
      when: 'just now',
      removable: true,
      attachments: [DEMO_SLIP],
    });
    return one(SALE_DETAIL());
  }
  if (method === 'POST' && /\/notifications\/read$/.test(p)) return one({ ok: true });

  return null;
}

export const DEMO_IDS = ID;
